import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { LOGO, LOGO_FULL, PHONE_DISPLAY, PHONE_DT, EMAIL, WEBSITE, ADDRESS } from "@/data/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const DEFAULT_SETTINGS = {
  company_name: "GROUPE AUTO LUXE",
  slogan: "La satisfaction client, notre credo",
  logo_url: LOGO,
  logo_full_url: LOGO_FULL,
  colors: { orange: "#F2620F", green: "#1E8A4C", dark_green: "#0F4D2A", heading: "" },
  phone: PHONE_DISPLAY,
  phone2: PHONE_DT,
  phones: [],
  social_links: [],
  recruit_delay: "5 jours ouvrés",
  font: "Public Sans",
  locations: [],
  loyalty: { enabled: true, code: "FIDELE10", percent: 10, text: "" },
  whatsapp: "+2250505541313",
  email: EMAIL,
  website: WEBSITE,
  address: ADDRESS,
  hours: "Lun – Sam : 8h00 – 18h00",
  social: { facebook: "", instagram: "", linkedin: "", tiktok: "", youtube: "", x: "" },
  hero: { badge: "", line1: "", line2: "", line3: "", sub: "" },
  announcement: "",
  content: { credo: "", services: [], faq: [], team: [], activity_images: [], service_images: [], activities: [], history: {}, recruit_testimonials: [], reply_templates: [] },
  promo: { enabled: false, title: "", text: "", link: "", button: "", image_url: "" },
  assistant: { chat: true, write: true, notes: "", welcome: "" },
  maintenance: { enabled: true, months: 6, text: "" },
  unpaid: { enabled: true, days: 7, max_reminders: 3 },
  payments: { note: "", holder: "", wave_link: "", methods: [] },
  sections: { marquee: true, credo: true, stats: true, activities: true, services: true, before_after: true, leadership: true, testimonials: true, partners: true, brands: true, faq: true, cta: true, whatsapp_fab: true, reviews_form: true, nav_track: true, nav_login: true, footer_admin: true, news: true, nav_news: true, news_external: true, history: true, vcards: true, recruit: true, maps: true },
  feeds: { ci: [], world: [], auto: [] },
};

const SettingsContext = createContext({ settings: DEFAULT_SETTINGS, refresh: () => {} });

const hexToRgb = (hex) => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
};

const shade = (hex, f) => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const c = (v) => Math.max(0, Math.min(255, Math.round(v * f)));
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => c(v).toString(16).padStart(2, "0")).join("")}`;
};

export const applyColors = (colors) => {
  const root = document.documentElement;
  const set = (name, hex) => { const rgb = hexToRgb(hex); if (rgb) root.style.setProperty(name, rgb); };
  set("--c-gold", colors.orange);
  set("--c-gold-hover", shade(colors.orange, 0.9));
  set("--c-brand", colors.green);
  set("--c-brand-hover", shade(colors.green, 0.82));
  set("--c-forest", colors.dark_green);
  set("--c-heading", colors.heading || colors.dark_green);
};

export const FONTS = ["Public Sans", "Manrope", "Figtree", "Source Sans 3", "Nunito Sans", "Work Sans", "Raleway", "Montserrat", "Playfair Display", "Lora"];

export const applyFont = (font) => {
  const f = FONTS.includes(font) ? font : "Public Sans";
  let link = document.getElementById("site-font");
  if (!link) { link = document.createElement("link"); link.id = "site-font"; link.rel = "stylesheet"; document.head.appendChild(link); }
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(f).replace(/%20/g, "+")}:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400&display=swap`;
  document.documentElement.style.setProperty("--font-main", `'${f}'`);
};

export const mediaUrl = (u) => (!u ? "" : u.startsWith("http") || u.startsWith("/logos") ? u : `${BACKEND_URL}${u}`);
export const waUrl = (settings) => `https://wa.me/${(settings.whatsapp || "").replace(/\D/g, "")}`;

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const refresh = () =>
    axios.get(`${BACKEND_URL}/api/settings`).then((r) => { setSettings(r.data); applyColors(r.data.colors); applyFont(r.data.font); }).catch(() => {});
  useEffect(() => { refresh(); }, []);
  return <SettingsContext.Provider value={{ settings, refresh }}>{children}</SettingsContext.Provider>;
};

export const useSettings = () => useContext(SettingsContext);
export const useVisible = (key) => { const { settings } = useSettings(); return settings.sections?.[key] !== false; };
